import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@components/layout/Header';
import Footer from '@components/layout/Footer';
import BackgroundLayer from '@components/layout/Background/BackgroundLayer';
import MobileBottomNav from '@components/layout/MobileBottomNav';
import { horoscopeService } from '@services/horoscopeService';
import { PAGE_SCHEMAS, SEOHead, getDynamicMetaTags } from '@/seo';

// Components
import PublicPageHeader from '@components/common/PublicPageHeader';
import { MoonStar } from 'lucide-react';
import SignSelector from './components/SignSelector';
import SidebarControls from './components/SidebarControls';
import HoroscopeDisplay from './components/HoroscopeDisplay';
import { useAuthStore } from '@store/authStore';
import { useSettingsStore } from '@store/settingsStore';
import { formatChartDateMonthly, getLocalISODate } from '@utils/dateUtils';
import { HOROSCOPE_TRANSLATIONS } from '../../../languages/horoscope';

const ZODIAC_SIGNS = [
    { name: 'Aries', image: '/zodiac_sign_icons/icons8-aries-96.png', element: 'Fire', dates: 'Mar 21 - Apr 19' },
    { name: 'Taurus', image: '/zodiac_sign_icons/icons8-taurus-96.png', element: 'Earth', dates: 'Apr 20 - May 20' },
    { name: 'Gemini', image: '/zodiac_sign_icons/icons8-gemini-96.png', element: 'Air', dates: 'May 21 - Jun 20' },
    { name: 'Cancer', image: '/zodiac_sign_icons/icons8-cancer-96.png', element: 'Water', dates: 'Jun 21 - Jul 22' },
    { name: 'Leo', image: '/zodiac_sign_icons/icons8-leo-96.png', element: 'Fire', dates: 'Jul 23 - Aug 22' },
    { name: 'Virgo', image: '/zodiac_sign_icons/icons8-virgo-96.png', element: 'Earth', dates: 'Aug 23 - Sep 22' },
    { name: 'Libra', image: '/zodiac_sign_icons/icons8-libra-96.png', element: 'Air', dates: 'Sep 23 - Oct 22' },
    { name: 'Scorpio', image: '/zodiac_sign_icons/icons8-scorpio-96.png', element: 'Water', dates: 'Oct 23 - Nov 21' },
    { name: 'Sagittarius', image: '/zodiac_sign_icons/icons8-sagittarius-96.png', element: 'Fire', dates: 'Nov 22 - Dec 21' },
    { name: 'Capricorn', image: '/zodiac_sign_icons/icons8-capricorn-96.png', element: 'Earth', dates: 'Dec 22 - Jan 19' },
    { name: 'Aquarius', image: '/zodiac_sign_icons/icons8-aquarius-96.png', element: 'Air', dates: 'Jan 20 - Feb 18' },
    { name: 'Pisces', image: '/zodiac_sign_icons/icons8-pisces-96.png', element: 'Water', dates: 'Feb 19 - Mar 20' },
];

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly';

const Horoscope = () => {
    const { lang: urlLang, period: urlPeriod, sign: urlSign, date: urlDate } = useParams();
    const navigate = useNavigate();

    const getProperSignName = (name: string | undefined) => {
        if (!name) return ZODIAC_SIGNS[0].name;
        const found = ZODIAC_SIGNS.find(s => s.name.toLowerCase() === name.toLowerCase());
        return found ? found.name : ZODIAC_SIGNS[0].name;
    };

    const { user, isAuthenticated } = useAuthStore();

    const [period, setPeriod] = useState<Period>((urlPeriod as Period) || 'daily');
    const [selectedSign, setSelectedSign] = useState<string>(getProperSignName(urlSign));
    const { contentLanguage, setContentLanguage } = useSettingsStore();

    // Language state - initialized from URL param, Zustand store settings
    const [language, setLanguage] = useState<'english' | 'hindi'>(() => {
        if (urlLang === 'hindi' || urlLang === 'english') {
            return urlLang as 'english' | 'hindi';
        }
        if (user?.content_language) {
            const lang = user.content_language.toLowerCase();
            return (lang === 'hindi' ? 'hindi' : 'english') as 'english' | 'hindi';
        }
        return contentLanguage;
    });

    // Listen to Zustand store changes for language
    useEffect(() => {
        let storeLang: 'english' | 'hindi' = contentLanguage;

        if (user?.content_language) {
            const userLang = user.content_language.toLowerCase();
            storeLang = (userLang === 'hindi' ? 'hindi' : 'english') as 'english' | 'hindi';
        }

        if (storeLang !== language) {
            setLanguage(storeLang);
        }
    }, [user?.content_language, contentLanguage]);

    const [loading, setLoading] = useState(false);
    const [horoscope, setHoroscope] = useState<any>(null);
    const [periodInfo, setPeriodInfo] = useState<string>('');

    // Get translations based on current language
    const t = HOROSCOPE_TRANSLATIONS[language === 'hindi' ? 'Hindi' : 'English'];

    // Correctly identify if the date in URL is a standard YYYY-MM-DD date
    const isValidDate = (d: string | undefined) => d ? /^\d{4}-\d{2}-\d{2}$/.test(d) : false;
    const today = getLocalISODate();
    const dailyDate = (period === 'daily' && isValidDate(urlDate)) ? urlDate! : today;

    const formatDateString = (dateStr: string) => {
        try {
            const dateObj = new Date(dateStr);
            const locale = language === 'hindi' ? 'hi-IN' : 'en-US';
            return new Intl.DateTimeFormat(locale, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            }).format(dateObj);
        } catch (e) {
            return dateStr;
        }
    };

    const getWeeklyRange = () => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay()); // Sunday
        const end = new Date(start);
        end.setDate(start.getDate() + 6); // Saturday
        const locale = language === 'hindi' ? 'hi-IN' : 'en-US';

        const formatPart = (d: Date) => {
            return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(d);
        };
        const year = start.getFullYear();
        return `${formatPart(start)} - ${formatPart(end)}, ${year}`;
    };

    // Initial sync and handling browser back/forward buttons
    useEffect(() => {
        if (urlLang && urlLang !== language) setLanguage(urlLang as 'english' | 'hindi');

        const properSign = getProperSignName(urlSign);
        if (properSign !== selectedSign) setSelectedSign(properSign);

        if (urlPeriod && urlPeriod !== period) setPeriod(urlPeriod as Period);
    }, [urlLang, urlPeriod, urlSign]);

    // Update URL when local state changes
    useEffect(() => {
        const signSlug = selectedSign.toLowerCase();
        let periodId = '';

        if (period === 'daily') {
            periodId = dailyDate;
        } else if (period === 'weekly') {
            periodId = (periodInfo || getWeeklyRange()).toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
        } else if (period === 'monthly') {
            periodId = (periodInfo || formatChartDateMonthly(new Date())).toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
        } else if (period === 'yearly') {
            periodId = periodInfo ? periodInfo.split(' ')[0] : new Date().getFullYear().toString();
        }

        const path = `/horoscope/${language}/${period}/${signSlug}/${periodId}`;

        const currentPath = window.location.pathname;
        if (currentPath !== path) {
            navigate(path, { replace: true });
            setContentLanguage(language);
        }
    }, [language, period, selectedSign, periodInfo, dailyDate, navigate, setContentLanguage]);

    useEffect(() => {
        if (selectedSign) {
            fetchHoroscope();
        }
    }, [period, selectedSign, language, dailyDate]); // Added dailyDate to dependencies

    const fetchHoroscope = async () => {
        if (!selectedSign) return;

        setLoading(true);
        try {
            let response;
            switch (period) {
                case 'daily':
                    response = await horoscopeService.getDailyHoroscope(language, selectedSign, dailyDate);
                    setPeriodInfo(formatDateString(response.date || dailyDate));
                    break;
                case 'weekly':
                    response = await horoscopeService.getWeeklyHoroscope(language, selectedSign);
                    setPeriodInfo(response.week || getWeeklyRange());
                    break;
                case 'monthly':
                    response = await horoscopeService.getMonthlyHoroscope(language, selectedSign);
                    const locale = language === 'hindi' ? 'hi-IN' : 'en-US';
                    setPeriodInfo(response.month || new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date()));
                    break;
                case 'yearly':
                    response = await horoscopeService.getYearlyHoroscope(language, selectedSign);
                    setPeriodInfo(response.year?.toString() || new Date().getFullYear().toString() + ' ' + t.yearlyForecastSuffix);
                    break;
            }

            if (response.success) {
                if (response.data?.horoscopes) {
                    const signKey = selectedSign.toLowerCase();
                    const signData = response.data.horoscopes[signKey] ||
                        response.data.horoscopes[selectedSign] ||
                        response.data.horoscopes[Object.keys(response.data.horoscopes).find(k => k.toLowerCase() === signKey) || ''];
                    setHoroscope(signData);
                } else if (response.data) {
                    setHoroscope(response.data);
                }
            }
        } catch (error) {
            console.error('Error fetching horoscope:', error);
        } finally {
            setLoading(false);
        }
    };

    const currentSignData = ZODIAC_SIGNS.find(s => s.name === selectedSign) || ZODIAC_SIGNS[0];

    return (
        <div className="min-h-screen bg-cosmic-950 text-purple-50 font-sans selection:bg-purple-900 selection:text-purple-100 relative pb-16 md:pb-0">
            {/* SEO Meta Tags */}
            <SEOHead
                {...getDynamicMetaTags(
                    'horoscope',
                    language === 'hindi' ? 'Hindi' : 'English',
                    {
                        sign: selectedSign,
                        period: period === 'daily' ? 'today' : period,
                        date: dailyDate
                    }
                )}
                language={language === 'hindi' ? 'hi' : 'en'}
                structuredData={PAGE_SCHEMAS.horoscope}
            />

            <BackgroundLayer />
            <Header />

            <main className="relative z-10 pt-14 md:pt-16 pb-16 md:pb-24 px-4 sm:px-6">
                <div className="container mx-auto max-w-7xl">
                    {/* Hero Section */}
                    <PublicPageHeader
                        title={period === 'weekly' ? t.weeklyHoroscope :
                            period === 'monthly' ? t.monthlyHoroscope :
                                period === 'yearly' ? t.yearlyHoroscope : t.dailyHoroscope}
                        highlightedText={period === 'weekly' ? t.weeklyHoroscope :
                            period === 'monthly' ? t.monthlyHoroscope :
                                period === 'yearly' ? t.yearlyHoroscope : t.dailyHoroscope}
                        description={period === 'weekly' ? t.weeklyDesc :
                            period === 'monthly' ? t.monthlyDesc :
                                period === 'yearly' ? t.yearlyDesc : t.dailyDesc}
                        icon={MoonStar}
                    />

                    {/* Sign Selector */}
                    <SignSelector
                        signs={ZODIAC_SIGNS}
                        selectedSign={selectedSign}
                        onSelectSign={setSelectedSign}
                        periodInfo={periodInfo}
                        language={language}
                    />

                    {/* Main Content Area */}

                    <div
                        key={selectedSign}
                        className="grid grid-cols-1 lg:grid-cols-12 gap-8"
                    >
                        {/* Left Sidebar - Controls & Quick Info */}
                        <SidebarControls
                            period={period}
                            onPeriodChange={setPeriod}
                            selectedSign={currentSignData}
                            language={language}
                        />

                        {/* Center - Main Horoscope Content */}
                        <div className="lg:col-span-9">
                            <HoroscopeDisplay
                                loading={loading}
                                horoscope={horoscope}
                                period={period}
                                periodInfo={periodInfo}
                                language={language}
                            />
                        </div>
                    </div>

                </div>
            </main>

            <Footer />
            <MobileBottomNav />
        </div>
    );
};

export default Horoscope;
